/**
 * $finalizedStrokes: the canvas's source-of-truth for committed strokes.
 *
 * M4 batch 7: client binary stroke transport.
 *
 * Before this store existed, the drawer's `DrawingCanvas` baked finalized
 * strokes directly into an offscreen "committed" bitmap. That worked for
 * pixel rendering but lost stroke identity, undo / clear / reconnect-hydrate
 * had no log to operate on. Audience clients had no way to receive replays
 * either since there was nothing to push into.
 *
 * This store holds a flat, ordered log of FinalizedStroke records. The
 * `<DrawingCanvas>` subscribes via `useStore` and replays the log onto its
 * committed bitmap whenever the log changes:
 *
 *   $finalizedStrokes  ─push/pop/clear─►  committedRef bitmap
 *                                              │
 *                                              └─►  visible canvas (blit)
 *
 * Producers:
 *   - Drawer pointer pipeline → `appendStroke()` on pointerup (local optimism).
 *   - Drawer fill click → `appendFill()` (local optimism; v4 T1 / D5).
 *   - WS layer → `appendStroke()` on STROKE_END_BROADCAST (audience replay).
 *   - WS layer → `appendFill()` on FILL_BROADCAST (audience replay; v4 T1).
 *   - WS layer → `popStrokeById()` on STROKE_UNDO_BROADCAST.
 *   - WS layer → `clearStrokes()` on STROKE_CLEAR_BROADCAST.
 *   - WS layer → `hydrateStrokes()` on ROOM_STATE_SYNC (reconnect / late-join).
 *
 * Dedup: `appendStroke()` is a no-op if a stroke with the same strokeId is
 * already in the log. The drawer optimistically appends on local pointerup
 * and the server broadcasts the same stroke back, without dedup the drawer
 * would render the stroke twice.
 *
 * Purity: this module owns no IO. Mutations are synchronous nanostore writes;
 * subscribers re-render on the next microtask.
 */

import { atom } from "nanostores";
import type { BrandColor } from "@drawlol/shared";

/**
 * A single finalized stroke. Self-contained so a subscriber can render it
 * with no external lookups. `path` is the SVG path produced by
 * `strokeToSvgPath()` with the brush width already baked in by
 * perfect-freehand (M5 fix in drawingEngine).
 */
export type FinalizedStroke = {
  kind: "stroke";
  strokeId: string;
  /** SVG path string produced by strokeToSvgPath with width baked in. */
  path: string;
  color: BrandColor;
  brushSize: 0 | 1 | 2;
  isEraser: boolean;
};

/**
 * A flood-fill op (v4 T1 / D5). Lives in the same ordered log as strokes so a
 * fill replays in sequence relative to the strokes beneath it (a fill samples
 * the canvas pixels under its hit point). `strokeId` mirrors a stroke's id so
 * undo/clear and the drawer's optimistic-echo dedup work uniformly.
 */
export type FinalizedFill = {
  kind: "fill";
  strokeId: string;
  /** Hit point in CSS-pixel canvas space. */
  point: { x: number; y: number };
  color: BrandColor;
};

/** One entry in the ordered canvas op log: a stroke or a fill. */
export type CanvasOp = FinalizedStroke | FinalizedFill;

export const $finalizedStrokes = atom<ReadonlyArray<CanvasOp>>([]);

/**
 * A remote stroke still in flight (audit 2026-06-11, live mid-stroke
 * render). The drawer flushes STROKE_BATCH samples every ~10 points and the
 * server already rebroadcasts them in real time; the receive side used to
 * drop them, so the audience canvas stayed empty until pointerup (measured:
 * 0 px during an open stroke). Provisional strokes hold those raw samples;
 * on STROKE_END the canonical smoothed path replaces them in the committed
 * log. Rendered ONLY by readOnly canvases, the drawer paints its own live
 * stroke locally, so its echo here is simply never drawn.
 */
export type ProvisionalStroke = {
  strokeIdNonce: number;
  color: BrandColor;
  brushSize: 0 | 1 | 2;
  isEraser: boolean;
  points: ReadonlyArray<{ x: number; y: number; pressure: number; pointerType: string }>;
};

export const $provisionalStrokes = atom<ReadonlyArray<ProvisionalStroke>>([]);

/** Open a provisional entry on STROKE_START (idempotent per nonce). */
export function beginProvisional(meta: Omit<ProvisionalStroke, "points">): void {
  const cur = $provisionalStrokes.get();
  if (cur.some((p) => p.strokeIdNonce === meta.strokeIdNonce)) return;
  $provisionalStrokes.set([...cur, { ...meta, points: [] }]);
}

/** Append a STROKE_BATCH's samples to its provisional entry (no-op if unknown). */
export function appendProvisionalSamples(
  strokeIdNonce: number,
  samples: ReadonlyArray<{ x: number; y: number; pressure: number; pointerType: string }>,
): void {
  const cur = $provisionalStrokes.get();
  const idx = cur.findIndex((p) => p.strokeIdNonce === strokeIdNonce);
  const entry = cur[idx];
  if (!entry) return;
  const next = cur.slice();
  next[idx] = { ...entry, points: [...entry.points, ...samples] };
  $provisionalStrokes.set(next);
}

/** Drop a provisional entry (STROKE_END committed the canonical path). */
export function endProvisional(strokeIdNonce: number): void {
  const cur = $provisionalStrokes.get();
  const next = cur.filter((p) => p.strokeIdNonce !== strokeIdNonce);
  if (next.length !== cur.length) $provisionalStrokes.set(next);
}

/** Wipe all provisional strokes (clear broadcast / snapshot hydrate). */
export function clearProvisionals(): void {
  if ($provisionalStrokes.get().length > 0) $provisionalStrokes.set([]);
}

/**
 * v4 T3: bumped whenever the canvas is RESET, a CLEAR or a full hydrate, as
 * opposed to an append/undo that merely edits the committed log. DrawingCanvas
 * subscribes and drops any in-progress stroke on a bump, so a stroke held
 * mid-clear/hydrate stops lingering as an overlay. This is deliberately a
 * SEPARATE signal from the log content: an UNDO that happens to empty the log
 * (popStrokeById removing the last committed op) must NOT abort an unrelated
 * active stroke: doing so would both drop the user's live ink and orphan its
 * server-side stroke buffer (STROKE_UNDO does not clear buffers), which
 * disconnect-synthesis could then resurrect. clearStrokes/hydrateStrokes bump;
 * popStrokeById does not.
 */
export const $strokeResetNonce = atom(0);

function bumpReset(): void {
  $strokeResetNonce.set($strokeResetNonce.get() + 1);
}

/** Dedup-by-id append. No-op if an op with the same strokeId is already logged. */
function appendOp(op: CanvasOp): void {
  const cur = $finalizedStrokes.get();
  if (cur.some((existing) => existing.strokeId === op.strokeId)) return;
  $finalizedStrokes.set([...cur, op]);
}

/**
 * Append a finalized stroke. No-op if a stroke with the same strokeId is
 * already in the log (drawer + server-broadcast dedup). The `kind: "stroke"`
 * discriminant is injected here so callers don't repeat it.
 */
export function appendStroke(s: Omit<FinalizedStroke, "kind">): void {
  appendOp({ kind: "stroke", ...s });
}

/**
 * Append a fill op (v4 T1). Same dedup as `appendStroke`: the drawer appends
 * optimistically on click and the server echoes FILL_BROADCAST with the same
 * op id, so without dedup the drawer would log (and re-flood-fill) twice. The
 * `kind: "fill"` discriminant is injected here.
 */
export function appendFill(f: Omit<FinalizedFill, "kind">): void {
  appendOp({ kind: "fill", ...f });
}

/**
 * Remove the op with the given id. Returns true if it was removed, false if no
 * matching op existed. Works on strokes and fills alike (both carry strokeId).
 */
export function popStrokeById(strokeId: string): boolean {
  const cur = $finalizedStrokes.get();
  const filtered = cur.filter((s) => s.strokeId !== strokeId);
  if (filtered.length === cur.length) return false;
  $finalizedStrokes.set(filtered);
  return true;
}

/**
 * Wipe the log. v4 T3: a USER-initiated clear (the default) also signals a
 * canvas reset so a held in-progress stroke is dropped (bump BEFORE the set so
 * the flush precedes the empty-replay repaint).
 *
 * Pass `resetInProgress = false` for the drawer's own STROKE_CLEAR_BROADCAST
 * echo (ws.ts): the optimistic local clear already flushed any held stroke, so
 * re-resetting on the echo would wrongly drop a NEW stroke the drawer began
 * during the echo round-trip. Audience clients are readOnly (no in-progress
 * stroke to flush), so the echo never needs the reset there either, it just
 * syncs the committed log.
 */
export function clearStrokes(resetInProgress: boolean = true): void {
  if (resetInProgress) bumpReset();
  $finalizedStrokes.set([]);
}

/** Return the id of the most-recently-appended op, or undefined if empty. */
export function lastStrokeId(): string | undefined {
  const cur = $finalizedStrokes.get();
  return cur[cur.length - 1]?.strokeId;
}

/**
 * Replace the entire log atomically. Used by the WS layer to hydrate from
 * `ROOM_STATE_SYNC.snapshot.state.openStrokes` on reconnect or late-join.
 */
export function hydrateStrokes(ops: ReadonlyArray<CanvasOp>): void {
  // v4 T3: a hydrate replaces the whole canvas with the server's truth, so any
  // local in-progress stroke is stale, signal a reset (bump before the set).
  bumpReset();
  $finalizedStrokes.set([...ops]);
}
